import { Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useState } from "react";
import { View, Text, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { DialogModal } from "@src/components/DialogModal";
import { useDeterrenceTest } from "@src/features/deterrence";
import { DETERRENCE_TEST_DEVICES, deterrenceTestPrompt, type DeterrenceDevice } from "@visioni/a3s-deterrence/app";

interface DeterrenceTestCardProps {
  siteId: string;
  cameraKey: string;
}

export function DeterrenceTestCard({ siteId, cameraKey }: DeterrenceTestCardProps) {
  const { theme } = useUnistyles();
  const deterrence = useDeterrenceTest(siteId, cameraKey);
  const [confirming, setConfirming] = useState<DeterrenceDevice | null>(null);
  const prompt = confirming ? deterrenceTestPrompt(confirming) : null;

  const handleConfirm = () => {
    if (confirming) deterrence.send(confirming);
    setConfirming(null);
  };

  return (
    <>
      <View bg="surfaceCard" borderRadius={theme.spacing.xl} borderWidth={1} borderColor="borderCard" p="5xl" gap="3xl">
        <Text textStyle="sm/semibold" color="textMuted">
          Manual Trigger
        </Text>
        {DETERRENCE_TEST_DEVICES.map((device) => (
          <Pressable
            key={device}
            onPress={() => setConfirming(device)}
            disabled={deterrence.isBusy(device)}
            accessibilityRole="button"
            accessibilityState={{ disabled: deterrence.isBusy(device) }}
          >
            <View
              flexDirection="row"
              alignItems="center"
              height={theme.spacing["12xl"]}
              bg="surfaceCardInner"
              borderRadius={theme.spacing.lg}
              borderWidth={1}
              borderColor="borderCard"
              px="3xl"
              py="lg"
              gap="xl"
              style={deterrence.isBusy(device) ? styles.busy : undefined}
            >
              <View
                width={theme.spacing["8xl"]}
                height={theme.spacing["8xl"]}
                alignItems="center"
                justifyContent="center"
              >
                <Icon name="siren" size={24} color={theme.colors.interactive.controls.default_midground} />
              </View>
              <Text textStyle="md/regular" color="text">
                {deterrenceTestPrompt(device).title}
              </Text>
            </View>
          </Pressable>
        ))}
      </View>

      <DialogModal
        visible={prompt !== null}
        onClose={() => setConfirming(null)}
        title={prompt?.title ?? ""}
        style={styles.dialog}
      >
        <Text textStyle="sm/regular" color="textMuted" mb="5xl">
          {prompt?.message}
        </Text>
        <View flexDirection="row" gap="xl" justifyContent="flex-end">
          <Button label="Cancel" onPress={() => setConfirming(null)} variant="secondary" variantColor="grey" />
          <Button label={prompt?.confirmLabel ?? ""} onPress={handleConfirm} variant="primary" variantColor="blue" />
        </View>
      </DialogModal>
    </>
  );
}

const styles = StyleSheet.create({
  busy: { opacity: 0.5 },
  dialog: { maxWidth: 440 },
});
